// Ported from eb-ui `src/legacy/OrganizerUi/OrganizerUiBanner.tsx` — the
// organizer-facing "you are managing this collection" banner (Edit / Private /
// Promote / Copy link). Rendered by `app.tsx` when
// `permissions.can_manage && !preview`.
//   - `@eventbrite/feature-flag-utils` `useFeatureFlags` → `@/lib/featureFlags`.
//   - `@eventbrite/legacy-infra.i18n` → `@/lib/gettext`; icons →
//     `../lib/iconography` (ArrowLeft is in the 142-icon bridge; LockChunky is
//     inlined).
// The `cc-organizer-banner*` / `cc-just-published-notification` classNames +
// the promote link + the GA tracking seams are preserved verbatim.
import React, { useContext } from 'react';

import { AnalyticsAction, AnalyticsCategory, trackEvent } from '../_shared';
import { AnalyticsContext, ApplicationContext } from '../context';
import { Icon } from '../lib/iconography';
import { ArrowLeft, LockChunky } from '../lib/iconography';
import { useFeatureFlags } from '@/lib/featureFlags';
import { gettext } from '@/lib/gettext';
import { CopyUrlButton } from './CopyUrlButton';

export const OrganizerUiBanner: React.FC = () => {
    const context = useContext(ApplicationContext);
    const { collectionId } = useContext(AnalyticsContext);
    const { launchPrivateCollections } = useFeatureFlags();

    // The banner only renders for a (non-moved) collection.
    const collection = (
        context as { collection?: { id: string; is_private?: boolean } } | null
    )?.collection;

    // BUGBOT (Low): the pending-publish value only means "just published" when
    // it's strictly the string 'true' — a stray `?just_published=0`/`=false`
    // must NOT celebrate. (The autocreated preview tip uses the same strict
    // check.)
    const justPublished =
        (
            context as {
                request?: { params?: Record<string, unknown> };
            } | null
        )?.request?.params?.just_published === 'true';
    const isCollectionPrivate = collection?.is_private;
    const promoteLink = `/organizations/marketing/boost-collection?collection_id=${collection?.id}&utm_source=collection-promote-cta&source=organizer-bottom-actions`;

    const onPromoteCollectionClick = (position: string) => {
        trackEvent({
            category: AnalyticsCategory.Listing,
            action: AnalyticsAction.CollectionPromote,
            label: position,
        });
    };

    return (
        <>
            <div
                className="cc-organizer-banner"
                role="banner"
                aria-labelledby="banner-text"
            >
                {collectionId && (
                    <div className="cc-organizer-banner__container eds-show-up-md">
                        <a
                            href={`/manage/collections/${collectionId}/publish`}
                            className="eds-text-bm eds-text-weight--heavy eds-text-color--control cc-organizer-banner__link"
                            onClick={() => {
                                trackEvent({
                                    action: AnalyticsAction.EditCollection,
                                    label: 'organizer-banner',
                                    category: AnalyticsCategory.Listing,
                                });
                            }}
                        >
                            <Icon
                                arial-label="hidden"
                                color="ui-blue--hover"
                                size="xsmall"
                                type={<ArrowLeft />}
                            />
                            <span className="eds-l-mar-left-3">
                                {gettext('Edit this collection')}
                            </span>
                        </a>
                    </div>
                )}
                <div className="cc-organizer-banner__container mid-container-content-alignment">
                    {launchPrivateCollections && isCollectionPrivate && (
                        <div
                            data-testid="collection-private-badge"
                            className="cc-organizer-banner__private-tag"
                        >
                            <Icon
                                color="ui-700"
                                size="small"
                                type={<LockChunky />}
                            />
                            <span className="cc-organizer-banner__text ">
                                {gettext('Private')}
                            </span>
                        </div>
                    )}
                </div>
                <div className="cc-organizer-banner__container">
                    <div className="cc-organizer-banner__buttons-wrapper">
                        <a
                            className="cc-organizer-banner__text"
                            href={promoteLink}
                            onClick={() =>
                                onPromoteCollectionClick('bottom-actions')
                            }
                        >
                            {gettext('Promote this collection')}
                        </a>
                        <CopyUrlButton />
                    </div>
                </div>
            </div>
            {justPublished && (
                <div
                    className="cc-just-published-notification"
                    data-testid="just-published-notification"
                >
                    <p>
                        {gettext(
                            "Congratulations! You've published your collection. 🎉",
                        )}
                    </p>
                </div>
            )}
        </>
    );
};
